/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { media } from 'src/styles/media';
import { places } from 'src/core/content-config/places';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import RouterLink from 'src/components/core/links/router-link';
import map from 'lodash/map';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Section` styled component.
 */

const Section = styled.section`
  padding-bottom: 5rem;

  ${media.min.md`
    padding-bottom: 13rem;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  letter-spacing: clamp(${14.4 / baseFontSize}rem, ${clampCalc(14.4, 19.2)}, ${19.2 / baseFontSize}rem);
  margin-bottom: 1.25rem;
  text-align: center;
  text-transform: uppercase;

  ${media.min.md`
    margin-bottom: 2.75rem;
  `}
`;

/**
 * `PlaceLink` styled component.
 */

const PlaceLink = styled.a.attrs(({ href }) => ({
  as: (href && RouterLink) || 'a'
}))`
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  display: block;
  font-family: var(--font-futura-pt-bold);
  font-size: clamp(55px, ${clampCalc(55, 75)}, 75px);
  letter-spacing: clamp(8.25px, ${clampCalc(8.25, 14.25)}, 14.25px);
  line-height: clamp(55px, ${clampCalc(55, 75)}, 75px);
  margin-bottom: 1.25rem;
  opacity: 1;
  outline: none;
  padding: 0;
  text-align: center;
  text-transform: uppercase;
  transition: opacity 0.35s ease;

  ${media.max.ms`
    font-size: 1.75rem;
    letter-spacing: 0.115625rem;
    line-height: 2rem;
  `}

  &:focus,
  &:hover {
    opacity: 0.5;
  }
`;

/**
 * Export `TripPlacesSection` component.
 */

const TripPlacesSection = () => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();

  return (
    <Section>
      <Container>
        <FadeInUpAnimation
          options={{
            distance: '100%',
            transitionTime: 1
          }}
        >
          <Title as={'h2'} variant={'header'}>
            {translate('trip-to-portugal:placesSection.title')}
          </Title>
        </FadeInUpAnimation>

        {map(places, ({ slug }, index: number) => (
          <FadeInUpAnimation
            key={index}
            options={{
              distance: '100%',
              transitionTime: 1
            }}
          >
            <PlaceLink href={routeResolver('tourDetails', { slug })}>
              {translate(`trip-to-portugal:places.${slug}`)}
            </PlaceLink>
          </FadeInUpAnimation>
        ))}
      </Container>
    </Section>
  );
};

/**
 * Export `TripPlacesSection` component.
 */

export default TripPlacesSection;
